/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import JSZip from "jszip";
import pako from "pako";
import { parseTarEntries } from "./TarUtils";

/**
 * Generic zip / tar.gz extraction for the upload modal. Filters by a caller-supplied
 * set of accepted extensions instead of a hardcoded .xml/.proto list, so any
 * uploader can share it. Nested archives are never returned.
 *
 * `acceptedExtensions` are dot-prefixed and lowercase, e.g. [".xml", ".proto"].
 */

const getExtension = (fileName: string): string => {
  const ext = fileName.split(".").pop();
  return ext ? `.${ext.toLowerCase()}` : "";
};

export const isZipFile = (file: File): boolean =>
  file.type === "application/zip" ||
  file.type === "application/x-zip-compressed" ||
  file.name.toLowerCase().endsWith(".zip");

export const isTarGzFile = (file: File): boolean =>
  file.type === "application/gzip" ||
  file.type === "application/x-gzip" ||
  file.name.toLowerCase().endsWith(".tar.gz");

export const isArchiveFile = (file: File): boolean =>
  isZipFile(file) || isTarGzFile(file);

// Keep a contained entry only if it is a supported, non-archive file.
const shouldKeepEntry = (
  name: string,
  acceptedExtensions: string[]
): boolean => {
  const lower = name.toLowerCase();
  // Never recurse into nested archives.
  if (lower.endsWith(".zip") || lower.endsWith(".tar.gz")) {
    return false;
  }
  return acceptedExtensions.includes(getExtension(name));
};

/** Extract every accepted, non-archive file from a zip, at any depth. */
export async function extractZipFile(
  zipFile: File,
  acceptedExtensions: string[]
): Promise<File[]> {
  const zip = await JSZip.loadAsync(zipFile);
  const extracted: File[] = [];

  for (const [fullPath, entry] of Object.entries(zip.files)) {
    if (entry.dir) {
      continue;
    }
    const name = fullPath.split("/").pop() || fullPath;
    if (!shouldKeepEntry(name, acceptedExtensions)) {
      continue;
    }
    const blob = await entry.async("blob");
    extracted.push(
      new File([blob], name, { type: blob.type || "application/octet-stream" })
    );
  }

  return extracted;
}

/** Extract every accepted, non-archive file from a .tar.gz, at any depth. */
export async function extractTarGzFile(
  tarGzFile: File,
  acceptedExtensions: string[]
): Promise<File[]> {
  const arrayBuffer = await tarGzFile.arrayBuffer();
  const tarBuffer = pako.ungzip(new Uint8Array(arrayBuffer));
  const extracted: File[] = [];

  for (const entry of parseTarEntries(tarBuffer)) {
    const name = entry.name.split("/").pop() || entry.name;
    if (!shouldKeepEntry(name, acceptedExtensions)) {
      continue;
    }
    // entry.data is a view into the decompressed buffer; slice() copies it so
    // the File owns its own bytes.
    extracted.push(
      new File([entry.data.slice()], name, {
        type: "application/octet-stream",
      })
    );
  }

  return extracted;
}

/**
 * Expand any archives in `files` into their accepted contained files; pass
 * non-archives through unchanged. A single archive that cannot be read rejects
 * with an error naming that archive, so callers can surface it per-file.
 */
export async function expandArchives(
  files: File[],
  acceptedExtensions: string[]
): Promise<File[]> {
  const result: File[] = [];

  for (const file of files) {
    if (isZipFile(file)) {
      result.push(...(await extractZipFile(file, acceptedExtensions)));
    } else if (isTarGzFile(file)) {
      result.push(...(await extractTarGzFile(file, acceptedExtensions)));
    } else {
      result.push(file);
    }
  }

  return result;
}
